import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Badge,
  Dropdown,
  DropdownButton,
  Modal,
  ListGroup,
  InputGroup,
  Collapse,
  Spinner,
  Alert,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTasks,
  faPlus,
  faEdit,
  faTrash,
  faCalendarAlt,
  faUsers,
  faFilter,
  faSort,
  faEllipsisV,
  faSearch,
  faCheck,
  faList,
  faClock,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import { Link, useLocation } from "react-router-dom";
import { getTasks, createTask, updateTask, deleteTask } from "../api/tasks";
import toast from "../utils/toastUtils";
import queryString from "query-string";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { getCommittees, getMembers, getMeetings } from "../api/api";
import { getProjects } from "../api/projects";
import LotusIcon from "../components/LotusIcon";
// In a real application, you'd implement API functions to fetch and manage tasks
// import { getTasks, createTask, updateTask, deleteTask } from '../api/api';

// Custom styles to ensure dropdowns aren't cut off
const styles = {
  dropdownMenu: {
    zIndex: 1050,
    position: "absolute",
    transform: "none",
    willChange: "transform",
  },
};

const TasksPage = () => {
  // State for tasks and loading
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get projectId from URL if it exists
  const location = useLocation();
  const { projectId } = queryString.parse(location.search);

  // Replace mock committees with state
  const [committees, setCommittees] = useState([]);

  // Replace mock meetings with state
  const [meetings, setMeetings] = useState([]);

  // State for task management
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [formMode, setFormMode] = useState("create"); // 'create' or 'edit'

  // Filter state
  const [filters, setFilters] = useState({
    status: "",
    priority: "",
    assignedTo: "",
    relatedType: "",
    search: "",
    sortBy: "dueDate",
    sortOrder: "asc",
  });

  // Get current user for createdBy field
  const { currentUser } = useAuth();

  // Add state for users
  const [users, setUsers] = useState([]);

  // Add state for projects
  const [availableProjects, setAvailableProjects] = useState([]);

  // Fetch users on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Use the getMembers API function instead of direct axios call
        const data = await getMembers();
        setUsers(data);
      } catch (err) {
        console.error("Failed to fetch users:", err);
        toast.error("Failed to load users");
      }
    };
    fetchUsers();
  }, []);

  // Add useEffect for fetching committees
  useEffect(() => {
    const fetchCommittees = async () => {
      try {
        const data = await getCommittees();
        setCommittees(data);
      } catch (err) {
        console.error("Failed to fetch committees:", err);
        toast.error("Failed to load committees");
      }
    };
    fetchCommittees();
  }, []);

  // Add useEffect for fetching meetings
  useEffect(() => {
    const fetchMeetings = async () => {
      try {
        const data = await getMeetings();
        setMeetings(data);
      } catch (err) {
        console.error("Failed to fetch meetings:", err);
        toast.error("Failed to load meetings");
      }
    };
    fetchMeetings();
  }, []);

  // Add useEffect for fetching projects
  useEffect(() => {
    const fetchAvailableProjects = async () => {
      try {
        const data = await getProjects();
        setAvailableProjects(data);
      } catch (err) {
        console.error("Failed to fetch projects:", err);
        toast.error("Failed to load projects");
      }
    };
    fetchAvailableProjects();
  }, []);

  // Update form state to handle multiple relationships
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    status: "To Do",
    priority: "Medium",
    dueDate: "",
    assignedTo: "",
    projectId: projectId || "",
    relationships: {
      project: null,
      committee: null,
      meeting: null,
    },
    labels: [],
    createdBy: currentUser?._id,
  });

  // Filter UI state - collapsible section
  const [showFilters, setShowFilters] = useState(false);

  // New label input
  const [newLabel, setNewLabel] = useState("");

  // Fetch tasks on component mount and when projectId changes
  useEffect(() => {
    fetchTasks();
  }, [projectId]);

  // Add a focus-based refresh for better synchronization between pages
  useEffect(() => {
    // Function to handle visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchTasks(); // Refresh tasks when page becomes visible again
      }
    };

    // Add event listener for visibility change
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Clean up the event listener
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Fetch tasks from API
  const fetchTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getTasks(projectId);
      setTasks(data);
    } catch (err) {
      setError(err.message || "Failed to fetch tasks");
      toast.error(err.message || "Failed to fetch tasks");
    } finally {
      setLoading(false);
    }
  };

  // Update handleCreateTask
  const handleCreateTask = () => {
    setFormData({
      title: "",
      description: "",
      status: "To Do",
      priority: "Medium",
      dueDate: "",
      assignedTo: "",
      projectId: projectId || "",
      relationships: {
        project: projectId
          ? {
              id: projectId,
              name:
                availableProjects.find((p) => p._id === projectId)?.title ||
                "Current Project",
              type: "project",
            }
          : null,
        committee: null,
        meeting: null,
      },
      labels: [],
      createdBy: currentUser?._id,
    });
    setFormMode("create");
    setShowTaskModal(true);
  };

  // Update handleEditTask
  const handleEditTask = (task) => {
    setCurrentTask(task);

    // Determine relationships from either relatedTo or relationships field
    const relationships = {
      project: null,
      committee: null,
      meeting: null,
    };

    // First check relatedTo field
    if (task.relatedTo?.type && task.relatedTo?.id && task.relatedTo?.name) {
      relationships[task.relatedTo.type] = {
        id: task.relatedTo.id,
        name: task.relatedTo.name,
        type: task.relatedTo.type,
      };
    }
    // Then check relationships field (if different from relatedTo)
    else if (
      task.relationships?.type &&
      task.relationships?.id &&
      task.relationships?.name
    ) {
      relationships[task.relationships.type] = {
        id: task.relationships.id,
        name: task.relationships.name,
        type: task.relationships.type,
      };
    }
    // Also handle projectId if it exists
    else if (task.projectId) {
      const project = availableProjects.find((p) => p._id === task.projectId);
      if (project) {
        relationships.project = {
          id: task.projectId,
          name: project.title,
          type: "project",
        };
      }
    }

    setFormData({
      title: task.title || "",
      description: task.description || "",
      status: task.status || "To Do",
      priority: task.priority || "Medium",
      dueDate: task.dueDate?.split("T")[0] || "",
      assignedTo: task.assignedTo?._id || "",
      projectId: task.projectId || "",
      relationships: relationships,
      labels: task.labels || [],
      createdBy: task.createdBy,
    });
    setFormMode("edit");
    setShowTaskModal(true);
  };

  // Delete a task
  const handleDeleteTask = async (taskId) => {
    if (window.confirm("Are you sure you want to delete this task?")) {
      try {
        await deleteTask(taskId);
        toast.success("Task deleted successfully");
        fetchTasks(); // Refresh the tasks list
      } catch (err) {
        toast.error(err.message || "Failed to delete task");
      }
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle relationship changes for tasks
  const handleRelationshipChange = (type, value) => {
    if (!value) {
      // If empty value, remove the relationship
      setFormData({
        ...formData,
        relationships: {
          ...formData.relationships,
          [type]: null,
        },
      });
      return;
    }

    let relationshipData = null;

    if (value) {
      switch (type) {
        case "project":
          const project = availableProjects.find((p) => p._id === value);
          if (project) {
            relationshipData = {
              id: project._id,
              name: project.title,
              type: "project", // Add type to the relationship data
            };
          }
          break;
        case "committee":
          const committee = committees.find((c) => c._id === value);
          if (committee) {
            relationshipData = {
              id: committee._id,
              name: committee.name,
              type: "committee", // Add type to the relationship data
            };
          }
          break;
        case "meeting":
          const meeting = meetings.find(
            (m) => m._id === value || m.id === parseInt(value)
          );
          if (meeting) {
            // Format date if needed
            const formattedDate = meeting.date
              ? typeof meeting.date === "string" && meeting.date.includes("T")
                ? new Date(meeting.date).toISOString().split("T")[0]
                : meeting.date
              : "";

            relationshipData = {
              id: meeting._id || meeting.id,
              name: meeting.name || meeting.title,
              date: formattedDate,
              type: "meeting", // Add type to the relationship data
            };
          }
          break;
      }
    }

    setFormData((prev) => ({
      ...prev,
      relationships: {
        ...prev.relationships,
        [type]: relationshipData,
      },
      // If it's a project, also update projectId
      ...(type === "project" && { projectId: relationshipData?.id || "" }),
    }));
  };

  // Add a new label
  const handleAddLabel = () => {
    if (newLabel.trim() && !formData.labels.includes(newLabel)) {
      setFormData({
        ...formData,
        labels: [...formData.labels, newLabel.trim()],
      });
      setNewLabel("");
    }
  };

  // Remove a label
  const handleRemoveLabel = (labelToRemove) => {
    setFormData({
      ...formData,
      labels: formData.labels.filter((label) => label !== labelToRemove),
    });
  };

  // Add a function that's more robust for retrieving task relationship data
  const getTaskRelationship = (task) => {
    // Return the first non-null relationship from either relationships or relatedTo field
    if (task.relationships?.name && task.relationships?.type) {
      return task.relationships;
    } else if (task.relatedTo?.name && task.relatedTo?.type) {
      return task.relatedTo;
    }
    return null;
  };

  // Update handleSubmitTask to ensure relationships are properly formatted
  const handleSubmitTask = async () => {
    try {
      // Validate required fields
      if (!formData.title) {
        toast.error("Task title is required");
        return;
      }

      if (!currentUser?._id) {
        toast.error("You must be logged in to create a task");
        return;
      }

      // Prepare the task data with only required title field
      const taskData = {
        title: formData.title,
        createdBy: currentUser._id, // Always include createdBy
      };

      // Add optional fields if they have values
      if (formData.description) taskData.description = formData.description;
      if (formData.status) taskData.status = formData.status;
      if (formData.priority) taskData.priority = formData.priority;

      // Handle date formatting
      if (formData.dueDate && formData.dueDate.trim()) {
        taskData.dueDate = new Date(formData.dueDate).toISOString();
      }

      // Make sure assignedTo is only added if it's a valid ObjectId
      if (formData.assignedTo && formData.assignedTo.trim()) {
        taskData.assignedTo = formData.assignedTo;
      }

      if (formData.labels?.length > 0) taskData.labels = formData.labels;

      // Handle relationships - Find the first non-null relationship
      const activeRelationship = Object.entries(formData.relationships).find(
        ([_, data]) => data !== null
      );

      if (activeRelationship) {
        const [type, data] = activeRelationship;

        // Ensure data has all required fields
        if (data && data.id && data.name && data.type) {
          // Set the relatedTo field for server-side
          taskData.relatedTo = {
            type: data.type,
            id: data.id,
            name: data.name,
          };

          // Also set the relationships field for client-side
          taskData.relationships = {
            type: data.type,
            id: data.id,
            name: data.name,
          };

          // For backwards compatibility with projectId
          if (data.type === "project") {
            taskData.projectId = data.id;
          }
        }
      }
      // Handle special case for project relationships when projectId is present
      else if (projectId) {
        const project = availableProjects.find((p) => p._id === projectId);
        if (project) {
          taskData.projectId = projectId;
          taskData.relatedTo = {
            type: "project",
            id: projectId,
            name: project.title,
          };
          // Also set relationships for consistency
          taskData.relationships = {
            type: "project",
            id: projectId,
            name: project.title,
          };
        }
      }

      console.log("Sending task data:", taskData);

      if (formMode === "create") {
        await createTask(taskData);
        toast.success("Task created successfully");
      } else {
        await updateTask(currentTask._id, taskData);
        toast.success("Task updated successfully");
      }
      setShowTaskModal(false);
      fetchTasks();
    } catch (err) {
      console.error("Task submission error:", err);
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        `Failed to ${formMode} task`;
      toast.error(errorMessage);
    }
  };

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  // Handle sort changes
  const handleSortChange = (field) => {
    if (filters.sortBy === field) {
      // Toggle sort order if clicking on the same field
      setFilters({
        ...filters,
        sortOrder: filters.sortOrder === "asc" ? "desc" : "asc",
      });
    } else {
      // Set new sort field with default ascending order
      setFilters({
        ...filters,
        sortBy: field,
        sortOrder: "asc",
      });
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      status: "",
      priority: "",
      assignedTo: "",
      relatedType: "",
      search: "",
      sortBy: "dueDate",
      sortOrder: "asc",
    });
  };

  // Filter and sort tasks
  const filteredAndSortedTasks = tasks
    .filter((task) => {
      return (
        (filters.status === "" || task.status === filters.status) &&
        (filters.priority === "" || task.priority === filters.priority) &&
        (filters.assignedTo === "" || task.assignedTo === filters.assignedTo) &&
        (filters.relatedType === "" ||
          task.relationships?.type === filters.relatedType ||
          task.relatedTo?.type === filters.relatedType) &&
        (filters.search === "" ||
          task.title.toLowerCase().includes(filters.search.toLowerCase()) ||
          task.description
            ?.toLowerCase()
            .includes(filters.search.toLowerCase()) ||
          task.labels?.some((label) =>
            label.toLowerCase().includes(filters.search.toLowerCase())
          ))
      );
    })
    .sort((a, b) => {
      if (filters.sortBy === "dueDate") {
        // Handle null or empty dates
        if (!a.dueDate) return filters.sortOrder === "asc" ? 1 : -1;
        if (!b.dueDate) return filters.sortOrder === "asc" ? -1 : 1;

        // Compare dates
        return filters.sortOrder === "asc"
          ? new Date(a.dueDate) - new Date(b.dueDate)
          : new Date(b.dueDate) - new Date(a.dueDate);
      } else if (filters.sortBy === "priority") {
        // Convert priority to numeric value for sorting
        const priorityValue = { High: 3, Medium: 2, Low: 1 };
        return filters.sortOrder === "asc"
          ? priorityValue[a.priority] - priorityValue[b.priority]
          : priorityValue[b.priority] - priorityValue[a.priority];
      } else {
        // Default string comparison for other fields
        return filters.sortOrder === "asc"
          ? a[filters.sortBy]?.localeCompare(b[filters.sortBy])
          : b[filters.sortBy]?.localeCompare(a[filters.sortBy]);
      }
    });

  // Get priority badge color
  const getPriorityBadgeColor = (priority) => {
    switch (priority) {
      case "High":
        return "danger";
      case "Medium":
        return "warning";
      case "Low":
        return "info";
      default:
        return "secondary";
    }
  };

  // Get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "To Do":
        return "secondary";
      case "In Progress":
        return "primary";
      case "Done":
        return "success";
      default:
        return "secondary";
    }
  };

  // Get related item badge color
  const getRelatedBadgeColor = (type) => {
    switch (type) {
      case "meeting":
        return "info";
      case "committee":
        return "warning";
      case "project":
        return "success";
      default:
        return "secondary";
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "No due date";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Add a new handler for quick status updates
  const handleQuickStatusChange = async (taskId, newStatus) => {
    try {
      await updateTask(taskId, { status: newStatus });
      toast.success("Status updated successfully");
      fetchTasks(); // Refresh the tasks list
    } catch (err) {
      toast.error(err.message || "Failed to update status");
    }
  };

  return (
    <Container className="py-3">
      <div className="section-title d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <LotusIcon className="me-3" size={32} />
          <h1 className="mb-0">{projectId ? "Project Tasks" : "All Tasks"}</h1>
        </div>
        <div>
          <Button
            variant="outline-primary"
            onClick={() => setShowFilters(!showFilters)}
            className="me-2"
            style={{
              borderColor: "#f5c2c2",
              color: "#d9669b",
            }}
          >
            <FontAwesomeIcon icon={faFilter} className="me-2" />
            {showFilters ? "Hide Filters" : "Show Filters"}
            <FontAwesomeIcon
              icon={showFilters ? faChevronUp : faChevronDown}
              className="ms-2"
              size="xs"
            />
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateTask}
            style={{
              background: "linear-gradient(to right, #e66b8f, #d4af37)",
              border: "none",
              fontWeight: "600",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            }}
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            New Task
          </Button>
        </div>
      </div>

      {/* Filters - Collapsible */}
      <Collapse in={showFilters}>
        <div>
          <Card
            className="shadow-sm border-0 mb-4"
            style={{ borderRadius: "12px", overflow: "hidden" }}
          >
            <Card.Header
              style={{
                background: "linear-gradient(to right, #fff0f5, #fff8dc)",
              }}
            >
              <h5 className="mb-0">
                <FontAwesomeIcon
                  icon={faFilter}
                  className="me-2"
                  style={{ color: "#d9669b" }}
                />
                Filter Tasks
              </h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={3} className="mb-3">
                  <Form.Group>
                    <Form.Label>Search</Form.Label>
                    <InputGroup>
                      <InputGroup.Text
                        style={{
                          background: "#fff0f5",
                          border: "1px solid #f5c2c2",
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faSearch}
                          style={{ color: "#d9669b" }}
                        />
                      </InputGroup.Text>
                      <Form.Control
                        name="search"
                        value={filters.search}
                        onChange={handleFilterChange}
                        placeholder="Search tasks..."
                        style={{ borderLeft: "none" }}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Group>
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      name="status"
                      value={filters.status}
                      onChange={handleFilterChange}
                    >
                      <option value="">All Statuses</option>
                      <option value="To Do">To Do</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Done">Done</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Group>
                    <Form.Label>Priority</Form.Label>
                    <Form.Select
                      name="priority"
                      value={filters.priority}
                      onChange={handleFilterChange}
                    >
                      <option value="">All Priorities</option>
                      <option value="High">High</option>
                      <option value="Medium">Medium</option>
                      <option value="Low">Low</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Group>
                    <Form.Label>Related To</Form.Label>
                    <Form.Select
                      name="relatedType"
                      value={filters.relatedType}
                      onChange={handleFilterChange}
                    >
                      <option value="">All Types</option>
                      <option value="meeting">Meetings</option>
                      <option value="committee">Committees</option>
                      <option value="project">Projects</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3} className="mb-3 d-flex align-items-end">
                  <DropdownButton
                    id="sort-dropdown"
                    title={
                      <span>
                        <FontAwesomeIcon icon={faSort} className="me-2" />
                        Sort: {filters.sortBy} (
                        {filters.sortOrder === "asc" ? "↑" : "↓"})
                      </span>
                    }
                    variant="outline-secondary"
                    className="me-2"
                    style={{ borderColor: "#f5c2c2", color: "#d9669b" }}
                  >
                    <Dropdown.Item onClick={() => handleSortChange("dueDate")}>
                      {filters.sortBy === "dueDate" && (
                        <FontAwesomeIcon
                          icon={faCheck}
                          className="me-2"
                          style={{ color: "#d9669b" }}
                        />
                      )}
                      Due Date
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleSortChange("priority")}>
                      {filters.sortBy === "priority" && (
                        <FontAwesomeIcon
                          icon={faCheck}
                          className="me-2"
                          style={{ color: "#d9669b" }}
                        />
                      )}
                      Priority
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleSortChange("status")}>
                      {filters.sortBy === "status" && (
                        <FontAwesomeIcon
                          icon={faCheck}
                          className="me-2"
                          style={{ color: "#d9669b" }}
                        />
                      )}
                      Status
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleSortChange("title")}>
                      {filters.sortBy === "title" && (
                        <FontAwesomeIcon
                          icon={faCheck}
                          className="me-2"
                          style={{ color: "#d9669b" }}
                        />
                      )}
                      Title
                    </Dropdown.Item>
                  </DropdownButton>
                  <Button
                    variant="outline-secondary"
                    onClick={handleClearFilters}
                    style={{ borderColor: "#f5c2c2", color: "#d9669b" }}
                  >
                    Clear Filters
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </div>
      </Collapse>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : (
        <>
          {/* Task List */}
          {filteredAndSortedTasks.length === 0 ? (
            <div className="text-center py-5 section">
              <FontAwesomeIcon
                icon={faTasks}
                style={{ fontSize: "3rem", color: "#d9669b", opacity: 0.5 }}
              />
              <h3 className="mt-3" style={{ color: "#d9669b" }}>
                No tasks found
              </h3>
              <p className="text-muted">
                {filters.search ||
                filters.status ||
                filters.priority ||
                filters.assignedTo ||
                filters.relatedType
                  ? "Try adjusting your filters"
                  : projectId
                  ? "Create your first task for this project"
                  : "Create your first task"}
              </p>
              <Button
                variant="primary"
                onClick={handleCreateTask}
                style={{
                  background: "linear-gradient(to right, #e66b8f, #d4af37)",
                  border: "none",
                }}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Create New Task
              </Button>
            </div>
          ) : (
            <div className="task-list">
              {filteredAndSortedTasks.map((task) => (
                <Card
                  key={task._id}
                  className="mb-4 shadow-sm border-0"
                  style={{
                    borderRadius: "12px",
                    overflow: "hidden",
                    borderLeft:
                      task.priority === "High"
                        ? "5px solid #dc3545"
                        : task.priority === "Medium"
                        ? "5px solid #ffc107"
                        : "5px solid #17a2b8",
                  }}
                >
                  <Card.Body className="py-4">
                    <div className="d-flex justify-content-between align-items-start">
                      <div style={{ maxWidth: "85%" }}>
                        <h5
                          style={{
                            color: "#d9669b",
                            textAlign: "left",
                            fontSize: "1.15rem",
                            marginBottom: "0.5rem",
                          }}
                        >
                          {task.title}
                        </h5>
                        {task.description && (
                          <p className="text-muted small mb-3">
                            {task.description}
                          </p>
                        )}
                      </div>
                      <DropdownButton
                        variant="light"
                        title={<FontAwesomeIcon icon={faEllipsisV} />}
                        id={`task-${task._id}-actions`}
                        size="sm"
                        align="end"
                      >
                        <Dropdown.Item onClick={() => handleEditTask(task)}>
                          <FontAwesomeIcon icon={faEdit} className="me-2" />
                          Edit
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => handleDeleteTask(task._id)}
                          className="text-danger"
                        >
                          <FontAwesomeIcon icon={faTrash} className="me-2" />
                          Delete
                        </Dropdown.Item>
                      </DropdownButton>
                    </div>

                    <div className="mt-3 d-flex flex-wrap gap-2">
                      <div style={{ position: "relative" }}>
                        <Dropdown>
                          <Dropdown.Toggle
                            variant={getStatusBadgeColor(task.status)}
                            size="sm"
                            className="sasc-badge"
                          >
                            <span className="me-1">{task.status}</span>
                            {task.status !== "Done" && (
                              <span className="ms-1">• {task.priority}</span>
                            )}
                          </Dropdown.Toggle>
                          <Dropdown.Menu style={styles.dropdownMenu}>
                            <Dropdown.Item
                              onClick={() =>
                                handleQuickStatusChange(task._id, "To Do")
                              }
                              active={task.status === "To Do"}
                            >
                              To Do
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={() =>
                                handleQuickStatusChange(task._id, "In Progress")
                              }
                              active={task.status === "In Progress"}
                            >
                              In Progress
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={() =>
                                handleQuickStatusChange(task._id, "Done")
                              }
                              active={task.status === "Done"}
                            >
                              Done
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>

                      {/* Display relationship badge - Fix for relationship badges not showing up */}
                      {(task.relationships?.name || task.relatedTo?.name) && (
                        <Badge
                          bg={getRelatedBadgeColor(
                            task.relationships?.type || task.relatedTo?.type
                          )}
                          className="me-2 sasc-badge"
                        >
                          <FontAwesomeIcon
                            icon={
                              (task.relationships?.type ||
                                task.relatedTo?.type) === "meeting"
                                ? faCalendarAlt
                                : (task.relationships?.type ||
                                    task.relatedTo?.type) === "committee"
                                ? faUsers
                                : faList
                            }
                            className="me-1"
                          />
                          {task.relationships?.name || task.relatedTo?.name}
                        </Badge>
                      )}
                    </div>

                    {task.labels && task.labels.length > 0 && (
                      <div className="mt-2">
                        {task.labels.map((label) => (
                          <Badge
                            key={label}
                            bg="light"
                            text="dark"
                            className="me-1 mb-1"
                            style={{ border: "1px solid #f5c2c2" }}
                          >
                            # {label}
                          </Badge>
                        ))}
                      </div>
                    )}

                    <hr
                      className="my-3"
                      style={{ borderColor: "#f5c2c2", opacity: 0.5 }}
                    />

                    <div className="d-flex justify-content-between align-items-center">
                      <div className="text-muted small">
                        <FontAwesomeIcon icon={faUsers} className="me-1" />
                        {task.assignedTo?.name || "Unassigned"}
                      </div>
                      <div className="text-muted small">
                        <FontAwesomeIcon icon={faClock} className="me-1" />
                        {formatDate(task.dueDate)}
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* Task Modal */}
      <Modal
        show={showTaskModal}
        onHide={() => setShowTaskModal(false)}
        size="lg"
        centered
      >
        <Modal.Header
          closeButton
          style={{ background: "linear-gradient(to right, #fff0f5, #fff8dc)" }}
        >
          <Modal.Title style={{ color: "#d9669b" }}>
            <FontAwesomeIcon icon={faTasks} className="me-2" />
            {formMode === "create" ? "Create New Task" : "Edit Task"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Task Title</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter task title"
                required
                style={{
                  backgroundColor: "#fafafa",
                  border: "1px solid #f5c2c2",
                }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter task description"
                style={{
                  backgroundColor: "#fafafa",
                  border: "1px solid #f5c2c2",
                }}
              />
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    style={{
                      backgroundColor: "#fafafa",
                      border: "1px solid #f5c2c2",
                    }}
                  >
                    <option value="To Do">To Do</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Done">Done</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Priority</Form.Label>
                  <Form.Select
                    name="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                    style={{
                      backgroundColor: "#fafafa",
                      border: "1px solid #f5c2c2",
                    }}
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Due Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="dueDate"
                    value={formData.dueDate}
                    onChange={handleInputChange}
                    style={{
                      backgroundColor: "#fafafa",
                      border: "1px solid #f5c2c2",
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Assigned To</Form.Label>
              <Form.Select
                name="assignedTo"
                value={formData.assignedTo}
                onChange={handleInputChange}
                style={{
                  backgroundColor: "#fafafa",
                  border: "1px solid #f5c2c2",
                }}
              >
                <option value="">Select Assignee</option>
                {users.map((user) => (
                  <option key={user._id} value={user._id}>
                    {user.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Relationships</Form.Label>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Project</Form.Label>
                    <Form.Select
                      value={formData.relationships.project?.id || ""}
                      onChange={(e) =>
                        handleRelationshipChange("project", e.target.value)
                      }
                      style={{
                        backgroundColor: "#fafafa",
                        border: "1px solid #f5c2c2",
                      }}
                    >
                      <option value="">Select a project</option>
                      {availableProjects.map((project) => (
                        <option key={project._id} value={project._id}>
                          {project.title}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Committee</Form.Label>
                    <Form.Select
                      value={formData.relationships.committee?.id || ""}
                      onChange={(e) =>
                        handleRelationshipChange("committee", e.target.value)
                      }
                      style={{
                        backgroundColor: "#fafafa",
                        border: "1px solid #f5c2c2",
                      }}
                    >
                      <option value="">Select a committee</option>
                      {committees.map((committee) => (
                        <option key={committee._id} value={committee._id}>
                          {committee.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Meeting</Form.Label>
                    <Form.Select
                      value={formData.relationships.meeting?.id || ""}
                      onChange={(e) =>
                        handleRelationshipChange("meeting", e.target.value)
                      }
                      style={{
                        backgroundColor: "#fafafa",
                        border: "1px solid #f5c2c2",
                      }}
                    >
                      <option value="">Select a meeting</option>
                      {meetings.map((meeting) => {
                        const meetingId = meeting._id || meeting.id;
                        const meetingName = meeting.name || meeting.title;
                        const meetingDate = meeting.date
                          ? typeof meeting.date === "string" &&
                            meeting.date.includes("T")
                            ? new Date(meeting.date).toLocaleDateString()
                            : meeting.date
                          : "";

                        return (
                          <option key={meetingId} value={meetingId}>
                            {meetingName}{" "}
                            {meetingDate ? `(${meetingDate})` : ""}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {/* Display selected relationships */}
              <div className="mt-2">
                {Object.entries(formData.relationships).map(([type, data]) => {
                  if (!data) return null;
                  return (
                    <Badge
                      key={type}
                      bg={getRelatedBadgeColor(type)}
                      className="me-2 mb-2"
                    >
                      <FontAwesomeIcon
                        icon={
                          type === "meeting"
                            ? faCalendarAlt
                            : type === "committee"
                            ? faUsers
                            : faList
                        }
                        className="me-1"
                      />
                      {data.name}
                      <span
                        onClick={() => handleRelationshipChange(type, "")}
                        style={{ cursor: "pointer", marginLeft: "8px" }}
                      >
                        ×
                      </span>
                    </Badge>
                  );
                })}
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Labels</Form.Label>
              <InputGroup className="mb-2">
                <Form.Control
                  type="text"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="Add a label"
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddLabel();
                    }
                  }}
                  style={{
                    backgroundColor: "#fafafa",
                    border: "1px solid #f5c2c2",
                  }}
                />
                <Button
                  variant="outline-primary"
                  onClick={handleAddLabel}
                  style={{ borderColor: "#f5c2c2", color: "#d9669b" }}
                >
                  Add
                </Button>
              </InputGroup>

              {formData.labels.length > 0 && (
                <div className="d-flex flex-wrap gap-1 mt-2">
                  {formData.labels.map((label) => (
                    <Badge
                      key={label}
                      bg="light"
                      text="dark"
                      className="mb-1 d-flex align-items-center"
                      style={{ border: "1px solid #f5c2c2" }}
                    >
                      # {label}
                      <span
                        onClick={() => handleRemoveLabel(label)}
                        style={{ cursor: "pointer", marginLeft: "5px" }}
                      >
                        &times;
                      </span>
                    </Badge>
                  ))}
                </div>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowTaskModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmitTask}
            style={{
              background: "linear-gradient(to right, #e66b8f, #d4af37)",
              border: "none",
            }}
          >
            {formMode === "create" ? "Create Task" : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default TasksPage;
